<template>
  <div class="min-h-screen bg-[#f8f7f4]">
    <section class="mx-auto max-w-3xl px-6 py-14">
      <div class="mb-10 text-center">
        <p class="text-xs font-semibold uppercase tracking-[0.25em] text-brand">Become a Vendor</p>
        <h1 class="mt-3 text-3xl font-semibold text-ink">Vendor Signup</h1>
        <p class="mt-2 text-sm text-muted">Create your vendor account with the details below.</p>
      </div>

      <form class="rounded-[28px] bg-white px-6 py-8 shadow-card ring-1 ring-black/5 sm:px-10" @submit.prevent="submit">
        <div class="space-y-6">
          <label class="field-block">
            <div class="field-shell" :class="fieldShellClass('brandName', focusedField === 'brandName', form.brandName)">
              <span class="field-floating-label">Brand name*</span>
              <input
                v-model.trim="form.brandName"
                type="text"
                class="field-control"
                @focus="focusedField = 'brandName'"
                @blur="focusedField = ''"
              >
            </div>
            <span v-if="errors.brandName" class="error-text">{{ errors.brandName }}</span>
          </label>

          <div class="field-block dropdown-wrap" ref="cityDropdownRef">
            <div
              class="field-shell field-shell-button"
              :class="fieldShellClass('city', focusedField === 'city' || openDropdown === 'city', form.city)"
            >
              <span class="field-floating-label">City (Choose your base city here)*</span>
              <button
                type="button"
                class="field-button"
                @click="toggleDropdown('city')"
              >
                <span :class="form.city ? 'text-ink' : 'text-transparent'">{{ form.city || 'Select city' }}</span>
                <i class="ri-arrow-down-s-line field-icon" :class="{ 'rotate-180': openDropdown === 'city' }"></i>
              </button>
            </div>
            <div v-if="openDropdown === 'city'" class="dropdown-panel">
              <div class="dropdown-search">
                <i class="ri-search-line text-sm text-muted"></i>
                <input
                  ref="citySearchRef"
                  v-model.trim="citySearch"
                  type="text"
                  class="dropdown-search-input"
                  placeholder="Search city"
                >
              </div>
              <button
                v-for="city in filteredCities"
                :key="city"
                type="button"
                class="dropdown-option"
                @click="selectDropdownValue('city', city)"
              >
                {{ city }}
              </button>
              <p v-if="!filteredCities.length" class="dropdown-empty">No cities found.</p>
            </div>
            <span v-if="errors.city" class="error-text">{{ errors.city }}</span>
          </div>

          <div class="field-block dropdown-wrap" ref="vendorTypeDropdownRef">
            <div
              class="field-shell field-shell-button"
              :class="fieldShellClass('vendorType', focusedField === 'vendorType' || openDropdown === 'vendorType', form.vendorType)"
            >
              <span class="field-floating-label">Select Vendor Type*</span>
              <button
                type="button"
                class="field-button"
                @click="toggleDropdown('vendorType')"
              >
                <span :class="form.vendorType ? 'text-ink' : 'text-transparent'">{{ form.vendorType || 'Select vendor type' }}</span>
                <i class="ri-arrow-down-s-line field-icon" :class="{ 'rotate-180': openDropdown === 'vendorType' }"></i>
              </button>
            </div>
            <div v-if="openDropdown === 'vendorType'" class="dropdown-panel">
              <div class="dropdown-search">
                <i class="ri-search-line text-sm text-muted"></i>
                <input
                  ref="vendorTypeSearchRef"
                  v-model.trim="vendorTypeSearch"
                  type="text"
                  class="dropdown-search-input"
                  placeholder="Search vendor type"
                >
              </div>
              <button
                v-for="type in filteredVendorTypes"
                :key="type"
                type="button"
                class="dropdown-option"
                @click="selectDropdownValue('vendorType', type)"
              >
                {{ type }}
              </button>
              <p v-if="!filteredVendorTypes.length" class="dropdown-empty">No vendor types found.</p>
            </div>
            <span v-if="errors.vendorType" class="error-text">{{ errors.vendorType }}</span>
          </div>

          <label class="field-block">
            <div class="field-shell" :class="fieldShellClass('email', focusedField === 'email', form.email)">
              <span class="field-floating-label">Email*</span>
              <input
                v-model.trim="form.email"
                type="email"
                class="field-control"
                @focus="focusedField = 'email'"
                @blur="focusedField = ''"
              >
            </div>
            <span v-if="errors.email" class="error-text">{{ errors.email }}</span>
          </label>

          <label class="field-block">
            <div
              class="field-shell phone-shell"
              :class="fieldShellClass('phone', focusedField === 'phone', form.phoneRaw)"
              @focusin="focusedField = 'phone'"
              @focusout="focusedField = ''"
            >
              <span class="field-floating-label">Phone number*</span>
              <input
                ref="phoneInputRef"
                type="tel"
                class="field-control phone-control"
                @input="syncPhone"
                @blur="syncPhone"
              >
            </div>
            <span v-if="errors.phone" class="error-text">{{ errors.phone }}</span>
          </label>

          <label class="field-block">
            <div class="field-shell" :class="fieldShellClass('password', focusedField === 'password', form.password)">
              <span class="field-floating-label">Password*</span>
              <input
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                class="field-control pr-12"
                @focus="focusedField = 'password'"
                @blur="focusedField = ''"
              >
              <button type="button" class="toggle-btn" @click="showPassword = !showPassword">
                <i :class="showPassword ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
              </button>
            </div>
            <span v-if="errors.password" class="error-text">{{ errors.password }}</span>
          </label>
        </div>

        <div class="mt-10 flex justify-end">
          <button
            type="submit"
            class="rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brandDark"
          >
            Sign up
          </button>
        </div>
      </form>
    </section>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, reactive, ref } from 'vue'
import intlTelInput from 'intl-tel-input'
import 'intl-tel-input/build/css/intlTelInput.css'

const cities = ['Mumbai', 'Delhi', 'Bengaluru', 'Hyderabad', 'Chennai', 'Pune', 'Kolkata', 'Ahmedabad']
const vendorTypes = ['Photographer', 'Caterer', 'Decorator', 'Venue', 'Planner', 'Makeup Artist', 'Entertainment', 'Transportation']

const form = reactive({
  brandName: '',
  city: '',
  vendorType: '',
  email: '',
  phone: '',
  phoneRaw: '',
  password: ''
})

const errors = reactive({})
const phoneInputRef = ref(null)
const phoneIti = ref(null)
const showPassword = ref(false)
const focusedField = ref('')
const openDropdown = ref('')
const citySearch = ref('')
const vendorTypeSearch = ref('')
const cityDropdownRef = ref(null)
const vendorTypeDropdownRef = ref(null)
const citySearchRef = ref(null)
const vendorTypeSearchRef = ref(null)

const filteredCities = computed(() => {
  const query = citySearch.value.toLowerCase()
  return cities.filter((city) => city.toLowerCase().includes(query))
})

const filteredVendorTypes = computed(() => {
  const query = vendorTypeSearch.value.toLowerCase()
  return vendorTypes.filter((type) => type.toLowerCase().includes(query))
})

const fieldShellClass = (key, focused, value) => ({
  'field-shell-active': focused || Boolean(value),
  'field-shell-error': Boolean(errors[key])
})

const validateForm = () => {
  Object.keys(errors).forEach((key) => delete errors[key])

  if (!form.brandName) errors.brandName = 'Brand name is required'
  if (!form.city) errors.city = 'City is required'
  if (!form.vendorType) errors.vendorType = 'Vendor type is required'
  if (!form.email) errors.email = 'Email is required'
  if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = 'Enter a valid email'
  if (!form.phoneRaw) errors.phone = 'Phone number is required'
  if (form.phoneRaw && phoneIti.value && !phoneIti.value.isValidNumber()) errors.phone = 'Enter a valid phone number'
  if (!form.password) errors.password = 'Password is required'
  if (form.password && form.password.length < 6) errors.password = 'Password must be at least 6 characters'

  return Object.keys(errors).length === 0
}

const syncPhone = () => {
  if (!phoneInputRef.value) return
  form.phoneRaw = phoneInputRef.value.value || ''
  form.phone = phoneIti.value?.isValidNumber() ? phoneIti.value.getNumber() : ''
}

const toggleDropdown = async (key) => {
  openDropdown.value = openDropdown.value === key ? '' : key
  focusedField.value = openDropdown.value || ''
  if (!openDropdown.value) return
  await nextTick()
  if (key === 'city' && citySearchRef.value) citySearchRef.value.focus()
  if (key === 'vendorType' && vendorTypeSearchRef.value) vendorTypeSearchRef.value.focus()
}

const selectDropdownValue = (key, value) => {
  form[key] = value
  if (key === 'city') citySearch.value = ''
  if (key === 'vendorType') vendorTypeSearch.value = ''
  openDropdown.value = ''
  focusedField.value = ''
}

const handleClickOutside = (event) => {
  const cityContains = cityDropdownRef.value?.contains(event.target)
  const vendorContains = vendorTypeDropdownRef.value?.contains(event.target)
  if (!cityContains && !vendorContains) {
    openDropdown.value = ''
    if (focusedField.value === 'city' || focusedField.value === 'vendorType') focusedField.value = ''
  }
}

const submit = () => {
  syncPhone()
  if (!validateForm()) return
  // Placeholder submit until API payload is defined.
  // eslint-disable-next-line no-console
  console.log('Vendor signup data', { ...form })
}

const initPhoneInput = () => {
  if (!phoneInputRef.value || phoneIti.value) return
  phoneIti.value = intlTelInput(phoneInputRef.value, {
    initialCountry: 'in',
    separateDialCode: true,
    nationalMode: false,
    preferredCountries: ['in', 'us', 'gb', 'ae'],
    autoPlaceholder: 'polite'
  })
  syncPhone()
}

onMounted(() => {
  initPhoneInput()
  document.addEventListener('mousedown', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside)
  if (phoneIti.value) {
    phoneIti.value.destroy()
    phoneIti.value = null
  }
})
</script>

<style scoped>
.field-block {
  display: block;
}

.field-shell {
  position: relative;
  min-height: 72px;
  border: 1px solid #d8dde5;
  border-radius: 18px;
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.field-shell-active {
  border-color: #ec2263;
  box-shadow: 0 0 0 4px rgba(236, 34, 99, 0.08);
}

.field-shell-error {
  border-color: #ef4444;
}

.field-floating-label {
  position: absolute;
  left: 18px;
  top: 24px;
  font-size: 16px;
  color: #9ca3af;
  pointer-events: none;
  transition: transform 0.18s ease, top 0.18s ease, font-size 0.18s ease, color 0.18s ease;
}

.field-shell-active .field-floating-label {
  top: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.field-control,
.field-button {
  width: 100%;
  height: 100%;
  min-height: 72px;
  border: 0;
  border-radius: 18px;
  background: transparent;
  padding: 28px 18px 12px;
  font-size: 16px;
  color: #111827;
}

.field-control:focus,
.field-button:focus,
.dropdown-search-input:focus {
  outline: none;
}

.field-shell-button {
  cursor: pointer;
}

.field-button {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  text-align: left;
}

.field-icon {
  color: #9ca3af;
  font-size: 18px;
  transition: transform 0.2s ease;
}

.dropdown-wrap {
  position: relative;
}

.dropdown-panel {
  margin-top: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.08);
  overflow: hidden;
}

.dropdown-search {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid #edf0f4;
}

.dropdown-search-input {
  width: 100%;
  border: 0;
  background: transparent;
  font-size: 14px;
  color: #111827;
}

.dropdown-option {
  display: block;
  width: 100%;
  padding: 13px 16px;
  border: 0;
  background: #fff;
  text-align: left;
  font-size: 14px;
  color: #111827;
  transition: background-color 0.15s ease;
}

.dropdown-option:hover {
  background: #f8fafc;
}

.dropdown-empty {
  padding: 16px;
  font-size: 14px;
  color: #94a3b8;
}

.phone-shell {
  overflow: visible;
  min-height: 88px;
}

.phone-control {
  padding-right: 18px;
}

.phone-shell .field-floating-label {
  left: 18px;
  top: 14px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.phone-shell.field-shell-active .field-floating-label {
  top: 14px;
}

.error-text {
  display: block;
  margin-top: 8px;
  font-size: 12px;
  color: #ef4444;
  font-weight: 600;
}

.toggle-btn {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
}

:deep(.iti) {
  width: 100%;
  height: 88px;
  display: flex;
  align-items: stretch;
}

:deep(.iti__selected-country) {
  height: 100%;
  display: flex;
  align-items: flex-end;
  padding-left: 18px;
  padding-bottom: 16px;
  background: transparent;
}

:deep(.iti--separate-dial-code .iti__selected-dial-code) {
  color: #374151;
  font-size: 16px;
  font-weight: 500;
  margin-left: 10px;
}

:deep(.iti__flag-container) {
  top: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: stretch;
}

:deep(.iti__country-list) {
  z-index: 50;
  width: 320px;
  max-width: min(320px, calc(100vw - 32px));
  max-height: 280px;
  margin-top: 10px;
  border: 1px solid #d8dde5;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.12);
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: thin;
}

:deep(.iti__country) {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
}

:deep(.iti__country-name) {
  color: #111827;
}

:deep(.iti__dial-code) {
  margin-left: auto;
  color: #94a3b8;
}

:deep(.iti__search-input),
:deep(.iti__search-input input),
:deep(.iti input[type='search']) {
  width: 100%;
  height: 44px;
  border: 0;
  border-bottom: 1px solid #e5e7eb;
  border-radius: 18px 18px 0 0;
  padding: 0 14px 0 38px;
  font-size: 14px;
  color: #111827;
  background: #fff;
  box-sizing: border-box;
}

:deep(.iti__search-input:focus),
:deep(.iti__search-input input:focus),
:deep(.iti input[type='search']:focus) {
  outline: none;
}

:deep(.iti__search-container) {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fff;
}

:deep(.iti__search-container)::before {
  content: '\ea6e';
  position: absolute;
  top: 50%;
  left: 14px;
  transform: translateY(-50%);
  font-family: remixicon;
  font-size: 15px;
  color: #9ca3af;
  pointer-events: none;
}

:deep(.iti__selected-country-primary) {
  height: 100%;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding-right: 16px;
  margin-right: 14px;
  position: relative;
}

:deep(.iti__selected-country-primary)::after {
  content: '';
  position: absolute;
  top: 36px;
  right: 0;
  bottom: 16px;
  width: 1px;
  background: #e5e7eb;
}

:deep(.iti__arrow) {
  margin-left: 2px;
}

:deep(.iti--separate-dial-code .iti__tel-input) {
  height: 88px;
  padding-top: 42px;
  padding-bottom: 16px;
  padding-left: 126px !important;
  border: 0;
  background: transparent;
  font-size: 16px;
  line-height: 1.2;
  color: #111827;
  box-sizing: border-box;
}

:deep(.iti input::placeholder) {
  color: transparent;
}
</style>
